<script>
/**
 * @component DoctorPortal
 * @description Pantalla de login split-screen para el portal médico.
 *   Izquierda: branding con gradiente y texto. Derecha: formulario de autenticación.
 *   Reutiliza authService.authenticate igual que Login.vue.
 * @example <router-view /> <!-- ruta /portal -->
 */
import logo from "@/mixins/logo";
import authService from "@/services/auth";
import { mapGetters } from "vuex";

export default {
  name: "DoctorPortal",

  mixins: [logo],

  data() {
    return {
      isLoading: false,
      username: "",
      password: "",
    };
  },

  computed: {
    ...mapGetters(["isPatient"]),
  },

  methods: {
    /**
     * @description Autentica al usuario. Si es paciente redirige a patient-me,
     *              de lo contrario al home o a la redirectUrl del query string.
     */
    signIn() {
      this.isLoading = true;
      authService
        .authenticate(this.username, this.password)
        .then(() => {
          if (this.isPatient) {
            this.$router.push({ name: "patient-me" });
            return;
          }
          const path =
            this.$route.query && this.$route.query.redirectUrl
              ? this.$route.query.redirectUrl
              : { name: "home" };
          this.$router.push(path);
        })
        .catch((err) => {
          if (err && err.message === "INVALID_AUDIENCE") {
            this.$message.error(
              "Tu usuario no tiene acceso a esta plataforma."
            );
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template lang="pug">
  .doctor-portal
    .doctor-portal__left
      .doctor-portal__brand
        svg.doctor-portal__icon(viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg")
          //- Cruz médica decorativa
          rect(x="24" y="8" width="16" height="48" rx="3" fill="rgba(255,255,255,0.15)")
          rect(x="8" y="24" width="48" height="16" rx="3" fill="rgba(255,255,255,0.15)")
          //- Estetoscopio estilizado
          circle(cx="32" cy="44" r="6" stroke="white" stroke-width="2" fill="none")
          path(d="M26 20 C26 12, 38 12, 38 20 L38 34" stroke="white" stroke-width="2" fill="none" stroke-linecap="round")
          path(d="M26 20 L26 30" stroke="white" stroke-width="2" fill="none" stroke-linecap="round")
        h1.doctor-portal__title Portal Médico
        p.doctor-portal__subtitle Accedé a tus consultas y atenciones

    .doctor-portal__right
      form.doctor-portal__form(@submit.prevent="signIn")
        .doctor-portal__logo
          micon.doctor-portal__logo-svg(:name="'quantux-logo-horizontal-nuevo'")
        h2.doctor-portal__heading Bienvenido, Doctor

        el-input#portal-username(
          placeholder="Ingresá tu usuario o DNI"
          autofocus="true"
          v-model="username"
          autocapitalize="none"
          prefix-icon="el-icon-user"
        )
        el-input#portal-password(
          type="password"
          placeholder="Contraseña"
          v-model="password"
          show-password
          prefix-icon="el-icon-lock"
        )
        el-button.doctor-portal__btn(
          native-type="submit"
          :disabled="!username || !password"
          :loading="isLoading"
        ) Iniciar Sesión

        router-link.doctor-portal__link(:to="{ name: 'password-forgot' }") ¿Olvidaste tu contraseña?
</template>

<style lang="scss" scoped>
.doctor-portal {
  display: flex;
  height: 100vh;
  width: 100%;
}

/* ── Mitad izquierda: branding ── */
.doctor-portal__left {
  flex: 1;
  background: linear-gradient(135deg, #002d5e, #0d9d9d);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.doctor-portal__brand {
  text-align: center;
  color: #fff;
}

.doctor-portal__icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
}

.doctor-portal__title {
  font-size: 2.2rem;
  font-weight: 700;
  margin: 0 0 12px;
}

.doctor-portal__subtitle {
  font-size: 1.1rem;
  opacity: 0.85;
  margin: 0;
}

/* ── Mitad derecha: formulario ── */
.doctor-portal__right {
  flex: 1;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.doctor-portal__form {
  width: 100%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.doctor-portal__logo {
  margin-bottom: 16px;
}

.doctor-portal__logo-svg {
  width: 240px !important;
  height: 36px !important;
}

.doctor-portal__heading {
  color: #002d5e;
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 28px;
}

.doctor-portal__form .el-input {
  margin-bottom: 14px;
  width: 100%;
}

.doctor-portal__btn {
  width: 100%;
  background-color: #0d9d9d !important;
  border-color: #0d9d9d !important;
  color: #fff !important;
  font-weight: 600;
  margin-top: 6px;

  &:hover,
  &:focus {
    background-color: #0b8a8a !important;
    border-color: #0b8a8a !important;
  }

  &.is-disabled {
    opacity: 0.6;
  }
}

.doctor-portal__link {
  margin-top: 18px;
  font-size: 0.9rem;
  color: #0d9d9d;

  &:hover {
    text-decoration: underline;
  }
}

/* ── Responsive: stack en mobile ── */
@media (max-width: 768px) {
  .doctor-portal {
    flex-direction: column;
  }

  .doctor-portal__left {
    flex: none;
    padding: 40px 20px;
  }

  .doctor-portal__right {
    flex: 1;
    padding: 32px 20px;
  }
}
</style>
